import React from 'react';
import Icon from '../../../components/AppIcon';

const SatisfactionAnalysisPanel = ({ analytics }) => {
  const satisfactionRating = analytics?.avgSatisfactionRating || 0;
  const ratingPercentage = (satisfactionRating / 5) * 100;

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 4.0) return 'text-blue-600';
    if (rating >= 3.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Good';
    if (rating >= 3.0) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Heart" className="w-5 h-5 text-blue-600" />
          Client Satisfaction Analysis
        </h3>
      </div>

      <div className="space-y-6">
        {/* Overall Rating */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 mb-4">
            <span className={`text-4xl font-bold ${getRatingColor(satisfactionRating)}`}>
              {satisfactionRating?.toFixed(1)}
            </span>
          </div>
          <p className="text-sm text-gray-600">Average Satisfaction Rating</p>
          <p className={`text-lg font-semibold mt-1 ${getRatingColor(satisfactionRating)}`}>
            {getRatingLabel(satisfactionRating)}
          </p>
        </div>

        {/* Rating Bar */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Overall Performance</span>
            <span className="text-sm font-medium text-gray-900">{ratingPercentage?.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                satisfactionRating >= 4.5 ? 'bg-green-500' :
                satisfactionRating >= 4.0 ? 'bg-blue-500' :
                satisfactionRating >= 3.0 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${ratingPercentage}%` }}
            />
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <Icon name="ThumbsUp" className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.resolvedTickets || 0}
            </p>
            <p className="text-xs text-gray-600">Resolved Tickets</p>
          </div>
          <div className="text-center">
            <Icon name="Clock" className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {analytics?.avgResolutionTimeHours || 0}h
            </p>
            <p className="text-xs text-gray-600">Avg Resolution</p>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">Improvement Recommendations</p>
              <ul className="mt-2 space-y-1 text-xs text-blue-800">
                {satisfactionRating < 4.0 && (
                  <li>• Focus on reducing response times</li>
                )}
                {analytics?.firstContactResolutionRate < 70 && (
                  <li>• Improve first contact resolution rate</li>
                )}
                {analytics?.slaComplianceRate < 90 && (
                  <li>• Enhance SLA compliance monitoring</li>
                )}
                {satisfactionRating >= 4.5 && (
                  <li>• Maintain excellent service standards</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SatisfactionAnalysisPanel;
